/**
 * Thinkers Arcade — Conversion & Event Tracking Engine
 * Tracks ACTUAL user engagement (page views, event triggers, conversions)
 * with direct Google Analytics 4 (GA4) & Google Ads (gtag.js) integration.
 * Zero mock / fake data: real tracking only.
 */

export interface AnalyticsEvent {
  id: string;
  type: 'page_view' | 'article_view' | 'cta_click' | 'conversion' | 'search_query';
  name: string;
  category: 'Conversion' | 'Engagement' | 'Navigation' | 'Search';
  label?: string;
  value?: number;
  metadata?: Record<string, any>;
  timestamp: string;
  isoDate: string;
  device: 'Mobile' | 'Desktop' | 'Tablet';
}

export interface ConversionSummary {
  totalPageViews: number;
  uniqueVisitors: number;
  totalArticleReads: number;
  totalCtaClicks: number;
  totalConversions: number;
  conversionRate: number; // percentage, e.g. 5.2%
  whatsappClicks: number;
  callClicks: number;
  formSubmissions: number;
  ordersCount: number;
  searchesCount: number;
}

export interface GoogleAdsConfig {
  ga4Id: string; // e.g. G-8472918471
  googleAdsId: string; // e.g. AW-114646341
  conversionLabel: string; // e.g. contact_submit_kes
  enabled: boolean;
}

export interface DailyTrendPoint {
  date: string;
  fullDate: string;
  pageViews: number;
  ctaClicks: number;
  conversions: number;
  totalEvents: number;
}

export interface FunnelStage {
  stage: string;
  count: number;
  dropoffRate: string;
  percentage: number;
}

const STORAGE_KEY_EVENTS = 'thinkers_analytics_events';
const STORAGE_KEY_CONFIG = 'thinkers_google_ads_config';
const STORAGE_KEY_VISITOR_ID = 'thinkers_visitor_session_id';

// Determine client device type
const getDeviceType = (): 'Mobile' | 'Desktop' | 'Tablet' => {
  if (typeof window === 'undefined') return 'Desktop';
  const width = window.innerWidth;
  if (width < 640) return 'Mobile';
  if (width < 1024) return 'Tablet';
  return 'Desktop';
};

// Get or create unique visitor ID per browser session
export const getVisitorId = (): string => {
  if (typeof window === 'undefined') return 'session-srv';
  try {
    let vid = sessionStorage.getItem(STORAGE_KEY_VISITOR_ID);
    if (!vid) {
      vid = `v-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 7)}`;
      sessionStorage.setItem(STORAGE_KEY_VISITOR_ID, vid);
    }
    return vid;
  } catch {
    return 'session-fallback';
  }
};

// Format friendly timestamp
const getFriendlyTimestamp = (): string => {
  const d = new Date();
  return (
    d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) +
    ' · ' +
    d.toLocaleDateString([], { month: 'short', day: 'numeric' })
  );
};

// Get Google Ads / GA4 Config
export const getGoogleAdsConfig = (): GoogleAdsConfig => {
  if (typeof window === 'undefined') {
    return { ga4Id: '', googleAdsId: '', conversionLabel: '', enabled: false };
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEY_CONFIG);
    if (raw) return JSON.parse(raw);
  } catch {
    // Fallback
  }
  return {
    ga4Id: 'G-THINKERSARCADE',
    googleAdsId: 'AW-114646341',
    conversionLabel: 'contact_submit_kes',
    enabled: true
  };
};

// Save Google Ads / GA4 Config
export const saveGoogleAdsConfig = (config: GoogleAdsConfig): void => {
  try {
    localStorage.setItem(STORAGE_KEY_CONFIG, JSON.stringify(config));
    if (config.enabled) {
      initializeGoogleAnalytics();
    }
  } catch {
    // Fallback
  }
};

// Initialize Google Analytics 4 Script in DOM
export const initializeGoogleAnalytics = (): void => {
  if (typeof window === 'undefined') return;
  const config = getGoogleAdsConfig();
  if (!config.enabled) return;

  const targetId = config.ga4Id || config.googleAdsId;
  if (!targetId || targetId.length < 4) return;

  const scriptId = 'google-analytics-gtag-script';
  if (!document.getElementById(scriptId)) {
    const script = document.createElement('script');
    script.id = scriptId;
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(targetId)}`;
    document.head.appendChild(script);

    const win = window as any;
    win.dataLayer = win.dataLayer || [];
    function gtag(...args: any[]) {
      win.dataLayer.push(args);
    }
    win.gtag = gtag;
    gtag('js', new Date());

    if (config.ga4Id) {
      gtag('config', config.ga4Id, {
        send_page_view: false,
        cookie_flags: 'SameSite=None;Secure'
      });
    }
    if (config.googleAdsId) {
      gtag('config', config.googleAdsId);
    }
  }
};

// Retrieve stored events from actual interactions (NO fake demo data)
export const getStoredEvents = (): AnalyticsEvent[] => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY_EVENTS);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
    }
  } catch {
    // Fallback
  }
  return [];
};

// Track a raw event
export const trackEvent = (
  eventData: Omit<AnalyticsEvent, 'id' | 'timestamp' | 'isoDate' | 'device'>
): AnalyticsEvent => {
  const newEvent: AnalyticsEvent = {
    ...eventData,
    id: `ev-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
    timestamp: getFriendlyTimestamp(),
    isoDate: new Date().toISOString(),
    device: getDeviceType()
  };

  if (typeof window !== 'undefined') {
    try {
      const current = getStoredEvents();
      // Keep up to 300 real events in rolling window
      const updated = [newEvent, ...current.slice(0, 299)];
      localStorage.setItem(STORAGE_KEY_EVENTS, JSON.stringify(updated));
      window.dispatchEvent(new CustomEvent('thinkers_analytics_event', { detail: newEvent }));
    } catch {
      // Fallback
    }

    // Google Ads / GA4 gtag.js dispatcher
    try {
      const gConfig = getGoogleAdsConfig();
      const win = window as any;
      if (typeof win.gtag === 'function' && gConfig.enabled) {
        // Standard GA4 event payload
        win.gtag('event', newEvent.name, {
          event_category: newEvent.category,
          event_label: newEvent.label,
          value: newEvent.value || 1,
          currency: 'KES',
          device_type: newEvent.device,
          ...(gConfig.googleAdsId && newEvent.type === 'conversion'
            ? { send_to: `${gConfig.googleAdsId}/${gConfig.conversionLabel}` }
            : {})
        });
      }
    } catch {
      // Ignore if blocked by ad-blocker
    }
  }

  return newEvent;
};

// Convenience tracking helpers
export const trackPageView = (pageName: string, path: string = window.location.pathname) => {
  return trackEvent({
    type: 'page_view',
    name: 'page_view',
    category: 'Navigation',
    label: pageName,
    metadata: { path, visitorId: getVisitorId() }
  });
};

export const trackArticleView = (articleId: string, articleTitle: string, category: string) => {
  return trackEvent({
    type: 'article_view',
    name: 'article_read',
    category: 'Engagement',
    label: articleTitle,
    metadata: { articleId, category, visitorId: getVisitorId() }
  });
};

export const trackCtaClick = (ctaName: string, label?: string, metadata?: Record<string, any>) => {
  const isHighConversion =
    ctaName.includes('whatsapp') ||
    ctaName.includes('call') ||
    ctaName.includes('paybill') ||
    ctaName.includes('order') ||
    ctaName.includes('quote');

  return trackEvent({
    type: isHighConversion ? 'conversion' : 'cta_click',
    name: ctaName,
    category: isHighConversion ? 'Conversion' : 'Engagement',
    label: label || ctaName,
    value: isHighConversion ? 1 : 0,
    metadata: { ...metadata, visitorId: getVisitorId() }
  });
};

export const trackConversion = (
  conversionName: string,
  label: string,
  value: number = 1,
  metadata?: Record<string, any>
) => {
  return trackEvent({
    type: 'conversion',
    name: conversionName,
    category: 'Conversion',
    label,
    value,
    metadata: { ...metadata, visitorId: getVisitorId() }
  });
};

// Calculate summary from actual event logs
export const getAnalyticsSummary = (): ConversionSummary => {
  const events = getStoredEvents();

  let totalPageViews = 0;
  let totalArticleReads = 0;
  let totalCtaClicks = 0;
  let whatsappClicks = 0;
  let callClicks = 0;
  let formSubmissions = 0;
  let ordersCount = 0;
  let searchesCount = 0;
  const uniqueVisitorSet = new Set<string>();

  events.forEach((e) => {
    if (e.metadata?.visitorId) {
      uniqueVisitorSet.add(e.metadata.visitorId);
    }
    if (e.type === 'page_view') totalPageViews++;
    if (e.type === 'article_view') totalArticleReads++;
    if (e.type === 'cta_click') totalCtaClicks++;
    if (e.name === 'whatsapp_click') whatsappClicks++;
    if (e.name === 'call_click') callClicks++;
    if (
      e.name === 'consultation_submit' ||
      e.name === 'lead_submit' ||
      e.name === 'quote_request' ||
      e.name === 'strategy_session_submit'
    ) {
      formSubmissions++;
    }
    if (
      e.name === 'hosting_plan_select' ||
      e.name === 'paybill_open' ||
      e.name === 'order_placed'
    ) {
      ordersCount++;
    }
    if (e.type === 'search_query') searchesCount++;
  });

  const uniqueVisitors =
    uniqueVisitorSet.size || (totalPageViews > 0 ? Math.max(1, Math.round(totalPageViews * 0.75)) : 0);
  const totalConversions = whatsappClicks + callClicks + formSubmissions + ordersCount;
  const conversionRate =
    totalPageViews > 0 ? Number(((totalConversions / totalPageViews) * 100).toFixed(1)) : 0;

  return {
    totalPageViews,
    uniqueVisitors,
    totalArticleReads,
    totalCtaClicks,
    totalConversions,
    conversionRate,
    whatsappClicks,
    callClicks,
    formSubmissions,
    ordersCount,
    searchesCount
  };
};

// Recharts Data Helpers — Trends over last 7 days from actual events
export const getDailyTrendsData = (events: AnalyticsEvent[]): DailyTrendPoint[] => {
  const pointsMap = new Map<string, DailyTrendPoint>();
  const now = new Date();

  // Prepopulate last 7 days so charts render full timelines
  for (let i = 6; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    const key = d.toISOString().split('T')[0];
    const shortLabel = d.toLocaleDateString([], { month: 'short', day: 'numeric' });
    pointsMap.set(key, {
      date: shortLabel,
      fullDate: key,
      pageViews: 0,
      ctaClicks: 0,
      conversions: 0,
      totalEvents: 0
    });
  }

  // Populate from actual event records
  events.forEach((e) => {
    const dateKey = (e.isoDate || new Date().toISOString()).split('T')[0];
    let point = pointsMap.get(dateKey);
    if (!point) {
      const d = new Date(dateKey);
      point = {
        date: isNaN(d.getTime()) ? dateKey : d.toLocaleDateString([], { month: 'short', day: 'numeric' }),
        fullDate: dateKey,
        pageViews: 0,
        ctaClicks: 0,
        conversions: 0,
        totalEvents: 0
      };
      pointsMap.set(dateKey, point);
    }

    point.totalEvents++;
    if (e.type === 'page_view') point.pageViews++;
    if (e.type === 'cta_click') point.ctaClicks++;
    if (e.type === 'conversion') point.conversions++;
  });

  return Array.from(pointsMap.values()).slice(-7);
};

// Recharts Data Helpers — Event Type Breakdown
export const getEventTypeBreakdown = (events: AnalyticsEvent[]): { name: string; count: number; color: string }[] => {
  const counts: Record<string, number> = {
    'Page Views': 0,
    'Article Reads': 0,
    'CTA Clicks': 0,
    'WhatsApp Inquiries': 0,
    'Phone Calls': 0,
    'Form Submissions': 0
  };

  events.forEach((e) => {
    if (e.name === 'page_view') counts['Page Views']++;
    else if (e.name === 'article_read') counts['Article Reads']++;
    else if (e.name === 'whatsapp_click') counts['WhatsApp Inquiries']++;
    else if (e.name === 'call_click') counts['Phone Calls']++;
    else if (e.name.includes('submit')) counts['Form Submissions']++;
    else counts['CTA Clicks']++;
  });

  return [
    { name: 'Page Views', count: counts['Page Views'], color: '#009fe3' },
    { name: 'Article Reads', count: counts['Article Reads'], color: '#a855f7' },
    { name: 'CTA Clicks', count: counts['CTA Clicks'], color: '#f59e0b' },
    { name: 'WhatsApp', count: counts['WhatsApp Inquiries'], color: '#10b981' },
    { name: 'Phone Calls', count: counts['Phone Calls'], color: '#38bdf8' },
    { name: 'Form Leads', count: counts['Form Submissions'], color: '#c59b4c' }
  ];
};

// Recharts Data Helpers — Device Distribution
export const getDeviceDistribution = (events: AnalyticsEvent[]): { name: string; value: number; color: string }[] => {
  const counts = { Mobile: 0, Desktop: 0, Tablet: 0 };
  events.forEach((e) => {
    if (e.device === 'Mobile') counts.Mobile++;
    else if (e.device === 'Tablet') counts.Tablet++;
    else counts.Desktop++;
  });

  return [
    { name: 'Mobile', value: counts.Mobile, color: '#009fe3' },
    { name: 'Desktop', value: counts.Desktop, color: '#c59b4c' },
    { name: 'Tablet', value: counts.Tablet, color: '#10b981' }
  ];
};

// Recharts Data Helpers — Conversion Funnel
export const getConversionFunnelStages = (summary: ConversionSummary): FunnelStage[] => {
  const visitors = Math.max(summary.uniqueVisitors, summary.totalPageViews > 0 ? 1 : 0);
  const views = summary.totalPageViews;
  const reads = summary.totalArticleReads;
  const clicks = summary.totalCtaClicks;
  const conversions = summary.totalConversions;

  return [
    {
      stage: 'Unique Visitors',
      count: visitors,
      dropoffRate: '100%',
      percentage: 100
    },
    {
      stage: 'Page Views',
      count: views,
      dropoffRate: visitors > 0 ? `${Math.round((views / Math.max(1, visitors)) * 100)}%` : '0%',
      percentage: visitors > 0 ? Math.min(100, Math.round((views / Math.max(1, visitors)) * 100)) : 0
    },
    {
      stage: 'Content Engagements',
      count: reads,
      dropoffRate: views > 0 ? `${Math.round((reads / Math.max(1, views)) * 100)}%` : '0%',
      percentage: views > 0 ? Math.min(100, Math.round((reads / Math.max(1, views)) * 100)) : 0
    },
    {
      stage: 'CTA Intent Clicks',
      count: clicks,
      dropoffRate: views > 0 ? `${Math.round((clicks / Math.max(1, views)) * 100)}%` : '0%',
      percentage: views > 0 ? Math.min(100, Math.round((clicks / Math.max(1, views)) * 100)) : 0
    },
    {
      stage: 'Qualified Leads & Conversions',
      count: conversions,
      dropoffRate: `${summary.conversionRate}%`,
      percentage: summary.conversionRate
    }
  ];
};

// Reset analytics
export const clearAnalyticsLogs = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY_EVENTS);
    window.dispatchEvent(new CustomEvent('thinkers_analytics_event', { detail: null }));
  } catch {
    // Fallback
  }
};
